// Connector-absent filtering: 3-layer defense
// 1. Backend (single_question_generation_item_service.py) — primary SQL filter
// 2. Frontend deserialization (SingleQuestion deserializeStoredQuestions) — defensive
// 3. Frontend polling (SingleQuestion/QuestionGenerationContext) — defensive
// All layers share the same detection logic via isConnectorAbsentResult().

export type GeneratedQuestionResultLike = {
  questionType?: string;
  question_type?: string;
  question?: string;
  content?: string;
  connector_absent_count?: number;
};

const CONNECTOR_QUESTION_TYPE_PREFIX = '연결어';
const CONNECTOR_ABSENT_RESULT_TEXT = '연결어 없음';

function normalizeQuestionType(result: GeneratedQuestionResultLike): string {
  return String(result.questionType ?? result.question_type ?? '').trim();
}

function normalizeQuestionText(result: GeneratedQuestionResultLike): string {
  return String(result.question ?? result.content ?? '').trim();
}

export function isConnectorAbsentResult(result: GeneratedQuestionResultLike): boolean {
  const questionType = normalizeQuestionType(result);
  const questionText = normalizeQuestionText(result);
  return questionType.startsWith(CONNECTOR_QUESTION_TYPE_PREFIX) && questionText === CONNECTOR_ABSENT_RESULT_TEXT;
}

export function filterOutConnectorAbsentResults<T extends GeneratedQuestionResultLike>(results: T[]): T[] {
  return results.filter((result) => !isConnectorAbsentResult(result));
}

export function countConnectorAbsentResults<T extends GeneratedQuestionResultLike>(results: T[]): number {
  return results.reduce((count, result) => {
    const aggregate = result.connector_absent_count;
    if (
      Object.keys(result).length === 1
      && Number.isSafeInteger(aggregate)
      && (aggregate ?? 0) > 0
    ) {
      return count + (aggregate ?? 0);
    }
    return count + (isConnectorAbsentResult(result) ? 1 : 0);
  }, 0);
}

const CONNECTOR_ABSENT_TOAST_MESSAGES = {
  workbook: {
    singular: '연결어가 없어 해당 문제는 "연결어 없음"으로 표시되었습니다.',
    plural: (n: number) => `연결어가 없어 ${n}개 항목이 "연결어 없음"으로 표시되었습니다.`,
  },
  single: {
    singular: '연결어가 없어 문제가 출제되지 않았습니다.',
    plural: (n: number) => `연결어가 없어 ${n}개 문제가 출제되지 않았습니다.`,
  },
} as const;

export function buildConnectorAbsentToastMessage(
  count: number,
  variant: 'workbook' | 'single',
): string {
  const template = CONNECTOR_ABSENT_TOAST_MESSAGES[variant];
  return count <= 1 ? template.singular : template.plural(count);
}
